// The diagnostics centre (docs/10 §§1-5). The brain owns diagnostics END TO
// END: the ring, redaction, batching, persistence, upload scheduling and
// backoff all live here, so all three clients behave identically and a fix
// lands everywhere at once. Hosts contribute exactly three things — the
// auto-send toggle state, coarse device context, and rendering — because those
// are OS-owned facts and native UI, and nothing else.

import type { KvStore } from '../../storage/kv.js';
import { logRing, pushLog } from '../logRing.js';
import { describeEvent, levelOf, type DiagEvent, type NetworkKind } from './events.js';
import { RingStore } from './ring.js';
import { ReportQueue } from './queue.js';
import { Consent } from './consent.js';
import {
  buildReport,
  eventsFromLines,
  isSafeToUpload,
  randomReportId,
  renderForCopy,
  usableEvents,
  type BuildContext,
  type DeviceContext,
  type QueuedReport,
  type ReportReason,
} from './report.js';
import { Uploader, type DiagSender, type UploadPayload } from './uploader.js';

export * from './events.js';
export * from './report.js';
export { Consent } from './consent.js';
export { ReportQueue, DIAG_QUEUE_KEY } from './queue.js';
export { RingStore, DIAG_RING_KEY } from './ring.js';
export { Uploader, defaultCompressor } from './uploader.js';
export type { DiagSender, UploadPayload, UploaderOptions } from './uploader.js';
export { restSender, intakeSender, socketSender, DIAG_REPORT_PATH } from './senders.js';
export {
  safeHost,
  safeHostFromUrl,
  pathTemplate,
  containsResidue,
  coarseSecond,
  coarseMinute,
} from './redact.js';

/**
 * Signaling has been unable to connect for this long ⇒ the app is unusable and
 * says so, with or without dev mode. Today it does the opposite: the field
 * incident showed a green dot and "you can call your friends" while nothing
 * worked. Silence there is its own defect (docs/10 §4).
 */
export const SEVERE_SIGNALING_MS = 3 * 60_000;
/** Consecutive HTTP failures with no success in between ⇒ every origin failing. */
export const SEVERE_HTTP_FAILURES = 6;
/**
 * How often "still not connected" is re-evaluated. A socket that never comes up
 * produces no state changes at all, so the unusable-app verdict cannot be
 * driven by events alone — the whole point of §4 is that a device stuck in
 * `connecting` must stop looking healthy.
 */
export const SEVERE_WATCHDOG_MS = 30_000;
/** How often a batch is cut while events keep arriving. */
export const CAPTURE_INTERVAL_MS = 5 * 60_000;

export type SeverePrompt = 'origins' | 'signaling';

/**
 * The additive, dev/prompt-only snapshot block (contract 2, docs/09 §5). Every
 * field is a count, a flag or a closed vocabulary — nothing here can carry an
 * identifier, which is why it is safe to project into a snapshot every host
 * decodes.
 */
export interface DiagSlice {
  /** A severe failure the host should surface and OFFER to report (§4). */
  prompt: SeverePrompt | null;
  /** The dev-unlock disclosure is owed and has not been shown (§4 obligation 1). */
  disclosure: boolean;
  autoSend: boolean;
  devMode: boolean;
  queued: number;
  lastOutcome: string | null;
}

export interface DiagCenterOptions {
  kv: KvStore;
  /** Where a rung's bytes go. Re-read per flush, so a rotated endpoint list
   *  changes the ladder without restarting anything. */
  senders: () => readonly DiagSender[];
  /** Uploads are never sent during a call (docs/10 §5). */
  inCall?: () => boolean;
  build?: BuildContext;
  now?: () => number;
  random?: () => number;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
  compress?: ((bytes: string) => Promise<Uint8Array | null>) | null;
  log?: (level: string, msg: string) => void;
  settleMs?: number;
  captureIntervalMs?: number;
}

export class DiagCenter {
  private readonly ring: RingStore;
  private readonly queueStore: ReportQueue;
  private readonly consentStore: Consent;
  private readonly uploader: Uploader;
  private readonly now: () => number;
  private readonly log: (level: string, msg: string) => void;
  private readonly build: BuildContext;
  private readonly captureIntervalMs: number;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;

  private device: DeviceContext = {};
  private inCallNow = false;
  private lastBatchedTs = 0;
  private captureTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private stopped = false;

  // Severe-failure watch (§4).
  private httpFailures = 0;
  private signalingDownSince: number | null = null;
  private promptedFor: SeverePrompt | null = null;
  private prompt: SeverePrompt | null = null;
  /** The report the live prompt was raised about. Preview, send and copy with
   *  no explicit id all address THIS one — a user answering "we noticed X"
   *  must not be shown, or send, some older report about something else. */
  private promptReportId: string | null = null;

  constructor(options: DiagCenterOptions) {
    this.now = options.now ?? Date.now;
    this.log = options.log ?? ((): void => undefined);
    this.build = options.build ?? {};
    this.captureIntervalMs = options.captureIntervalMs ?? CAPTURE_INTERVAL_MS;
    this.setTimeoutFn = options.setTimeoutFn ?? setTimeout;
    this.clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;

    this.ring = new RingStore({
      kv: options.kv,
      // The same "not during a call" fact that gates uploads gates the ring's
      // WRITE RATE: on iOS a flush is a synchronous Keychain call on the thread
      // the call state machine runs on (docs/10 §5, prime directive).
      busy: options.inCall ?? ((): boolean => this.inCallNow),
      ...(options.setTimeoutFn ? { setTimeoutFn: options.setTimeoutFn } : {}),
      ...(options.clearTimeoutFn ? { clearTimeoutFn: options.clearTimeoutFn } : {}),
    });
    this.queueStore = new ReportQueue({ kv: options.kv });
    this.consentStore = new Consent({
      kv: options.kv,
      ...(options.now ? { now: options.now } : {}),
    });
    this.uploader = new Uploader({
      queue: this.queueStore,
      consent: this.consentStore,
      senders: options.senders,
      inCall: options.inCall ?? ((): boolean => this.inCallNow),
      ...(options.now ? { now: options.now } : {}),
      ...(options.random ? { random: options.random } : {}),
      ...(options.setTimeoutFn ? { setTimeoutFn: options.setTimeoutFn } : {}),
      ...(options.clearTimeoutFn ? { clearTimeoutFn: options.clearTimeoutFn } : {}),
      ...(options.compress !== undefined ? { compress: options.compress } : {}),
      ...(options.settleMs !== undefined ? { settleMs: options.settleMs } : {}),
      log: this.log,
    });
  }

  /** Restore the ring, the queue and the consent state. Idempotent. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    const restored = await this.ring.attach();
    await this.queueStore.load();
    await this.consentStore.load();
    // Restored lines ARE eligible for the next batch. A line that already rode
    // a report may be reported twice, and that is the right trade: the report
    // it rode is, by construction, one that may never have been delivered —
    // this whole module exists because it usually is not.
    this.lastBatchedTs = 0;
    if (restored > 0) this.log('info', `diag: ${restored} log lines restored from the last session`);
    this.armCapture();
    this.armWatchdog();
    this.uploader.schedule();
  }

  stop(): void {
    this.stopped = true;
    this.disarmCapture();
    if (this.watchdogTimer !== null) {
      this.clearTimeoutFn(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.uploader.stop();
    this.ring.detach();
  }

  /** Write-through for the ring so a crash keeps the last lines. */
  flushRing(): Promise<void> {
    return this.ring.flush();
  }

  // --- recording ------------------------------------------------------------

  /**
   * Record a structured event. The human `msg` is derived from the ALREADY
   * REDACTED event, so the on-screen line and the uploadable payload can never
   * disagree about what was safe to say.
   */
  record(event: DiagEvent): void {
    pushLog(levelOf(event), describeEvent(event), event);
    this.watchSevere(event);
  }

  /** Coarse device context — OS-owned facts only, supplied by the host (§1). */
  setDeviceContext(device: DeviceContext): void {
    this.device = { ...this.device, ...device };
  }

  deviceContext(): DeviceContext {
    return { ...this.device };
  }

  networkKind(): NetworkKind | undefined {
    return this.device.networkKind as NetworkKind | undefined;
  }

  /** Told by the engine. Gates every upload (§5) and nothing else. */
  noteCall(inCall: boolean): void {
    this.inCallNow = inCall;
  }

  /**
   * Told by the engine on every signaling status change. "Unable to connect for
   * minutes" is one of the three definitions of unusable in §4.
   */
  noteSignaling(connected: boolean): void {
    if (connected) {
      this.signalingDownSince = null;
      if (this.prompt === 'signaling') this.prompt = null;
      return;
    }
    if (this.signalingDownSince === null) this.signalingDownSince = this.now();
    this.checkSignalingAge();
  }

  private checkSignalingAge(): void {
    const since = this.signalingDownSince;
    if (since !== null && this.now() - since >= SEVERE_SIGNALING_MS) this.raise('signaling');
  }

  private watchSevere(event: DiagEvent): void {
    if (event.event !== 'http.attempt') return;
    if (event.outcome === 'ok' || event.outcome === 'cancelled') {
      this.httpFailures = 0;
      if (this.prompt === 'origins') this.prompt = null;
      return;
    }
    this.httpFailures += 1;
    if (this.httpFailures >= SEVERE_HTTP_FAILURES) this.raise('origins');
  }

  private raise(prompt: SeverePrompt): void {
    if (this.prompt === prompt || this.promptedFor === prompt) return;
    this.prompt = prompt;
    this.promptedFor = prompt;
    this.log('warn', `diag: severe failure (${prompt}) — offering a report`);
    // The popup is automatic; the upload is not. `capture` queues the report so
    // the user can preview and send it; outside dev mode it sits there until
    // they tap, and even inside dev mode it obeys the toggle.
    //
    // The id is RESERVED here rather than read back from the queue write: the
    // prompt is already on screen, and a user who taps "preview" before the KV
    // round-trip lands must not be shown some older report instead.
    const reportId = randomReportId();
    this.promptReportId = reportId;
    void this.capture('severe', reportId).then((report) => {
      if (report === null) this.promptReportId = null;
    });
  }

  /** The user answered the severe-failure prompt (either way). */
  dismissPrompt(): void {
    this.prompt = null;
    this.promptReportId = null;
  }

  // --- consent (§4) ---------------------------------------------------------

  consent(): Consent {
    return this.consentStore;
  }

  async setDevMode(on: boolean): Promise<void> {
    await this.consentStore.setDevMode(on);
    // Automatic batching starts and stops with the consent that could ever use
    // it — see `armCapture`.
    this.armCapture();
  }

  async setAutoSend(on: boolean): Promise<void> {
    await this.consentStore.setAutoSend(on);
    this.armCapture();
  }

  markDisclosed(): Promise<void> {
    return this.consentStore.markDisclosed();
  }

  // --- batching and delivery ------------------------------------------------

  queue(): ReportQueue {
    return this.queueStore;
  }

  /**
   * Cut a batch: everything structured the ring has gathered since the last
   * one, as one redacted document, queued for delivery. Returns null when there
   * is nothing to say.
   *
   * `reportId` lets a caller RESERVE the id before the queue write lands — the
   * severe prompt does, so the offer on screen and the document behind it are
   * the same thing from the first frame.
   */
  async capture(reason: ReportReason = 'auto', reportId?: string): Promise<QueuedReport | null> {
    const lines = logRing();
    // A batch cut BECAUSE something went wrong, or because the user asked, takes
    // the WHOLE ring rather than the slice since the last automatic batch: the
    // session is what the reader needs, and a severe prompt landing seconds
    // after an auto batch would otherwise describe those seconds only.
    const events = usableEvents(eventsFromLines(lines, reason === 'auto' ? this.lastBatchedTs : 0));
    // No events ⇒ NO REPORT, whatever the reason. `diag.Parse` rejects an
    // empty-events body outright (`reason_no_events`), so queueing one only
    // spends the retry ladder of a device that may be blocked and ends in the
    // same place. Same for a report whose every event failed the clock bounds.
    if (events.length === 0) return null;
    const atMs = this.now();
    this.lastBatchedTs = atMs;
    const body = buildReport({
      reportId: reportId ?? randomReportId(),
      reason,
      device: this.device,
      build: this.build,
      events,
    });
    const report = await this.queueStore.enqueue({
      body,
      atMs,
      reason,
      // A report cut BECAUSE the user was asked is not consented until they say
      // so; §4 is explicit that outside dev mode the sending needs the tap.
      consented: false,
    });
    this.uploader.schedule();
    return report;
  }

  /**
   * The exact bytes that would be uploaded (§4). A promise of anonymisation the
   * user cannot inspect is not a promise — so this is the same function the
   * uploader hands to a sender, not a rendering of it.
   */
  preview(id?: string): Promise<UploadPayload | null> {
    const target = this.targetId(id);
    return target === undefined ? Promise.resolve(null) : this.uploader.preview(target);
  }

  /**
   * The user tapped "send". This is the ONLY path by which anything leaves a
   * device that is not in dev mode with auto-send on.
   */
  async sendNow(id?: string): Promise<void> {
    const target = this.targetId(id);
    if (target === undefined) return;
    // `consent` grants and clears the backoff WITHOUT touching the document —
    // the bytes the user just previewed must be the bytes that go.
    await this.queueStore.consent(target);
    await this.uploader.flush();
  }

  /**
   * Rung 5 (docs/10 §5): the report as a person reads it, for a device that can
   * reach no rung above this one. It is the uploaded document PLUS the delivery
   * history — how long it spent trying — which the intake has no field for and
   * so cannot carry itself.
   */
  copyText(id?: string): string | null {
    const target = this.targetId(id);
    const report = this.queueStore.list().find((r) => r.id === target);
    if (!report) return null;
    // Copy is EGRESS — the user pastes it into a support channel — so it passes
    // the same §3 gate the uploader applies, on the bytes that actually leave:
    // for rung 5 that is the document PLUS its delivery history. A report the
    // app refuses to UPLOAD must not be handed out by hand instead.
    const text = renderForCopy(report);
    if (!isSafeToUpload(text)) {
      this.log('error', 'diag: report withheld — failed the redaction gate');
      return null;
    }
    return text;
  }

  /**
   * Which report an id-less preview/send/copy is about: the one the live prompt
   * was raised about while it is still queued, else the oldest. All three go
   * through here, so they cannot disagree.
   */
  private targetId(id?: string): string | undefined {
    if (id !== undefined) return id;
    const queued = this.queueStore.list();
    const prompted = queued.find((r) => r.id === this.promptReportId);
    return prompted?.id ?? queued[0]?.id;
  }

  flush(): Promise<void> {
    return this.uploader.flush();
  }

  slice(): DiagSlice {
    return {
      prompt: this.prompt,
      disclosure: this.consentStore.disclosurePending(),
      autoSend: this.consentStore.autoSend(),
      devMode: this.consentStore.isDevMode(),
      queued: this.queueStore.size(),
      lastOutcome: this.uploader.lastDeliveryOutcome(),
    };
  }

  /** Omit the block entirely when there is nothing to say, so a snapshot the
   *  host already knows how to decode stays byte-identical (docs/09 §5). */
  sliceOrUndefined(): DiagSlice | undefined {
    const slice = this.slice();
    const quiet =
      slice.prompt === null &&
      !slice.disclosure &&
      !slice.devMode &&
      slice.queued === 0 &&
      slice.lastOutcome === null;
    return quiet ? undefined : slice;
  }

  /**
   * Automatic batching runs only while a batch could ever be sent — dev mode
   * with auto-send on. Outside that, `mode()` is `'ask'` and every automatic
   * batch is a report that can never leave on its own: a KV rewrite of the
   * whole queue slot every five minutes, permanently, for nothing. The reports
   * a non-dev device does produce are cut on demand — by the severe prompt
   * (`raise`) or by a host "send report" tap — and those take the whole ring,
   * so nothing is lost by not batching in between.
   */
  private armCapture(): void {
    // Withdrawn consent DISARMS the timer that is already pending, rather than
    // merely declining to arm the next one: a pending timer would cut one more
    // batch — a whole queue slot rewritten to KV — after the user said stop, and
    // a consent that takes effect one batch later is not consent (§4).
    if (this.stopped || !this.started || !this.consentStore.allowsAutomatic()) {
      this.disarmCapture();
      return;
    }
    if (this.captureTimer !== null) return;
    this.captureTimer = this.setTimeoutFn(() => {
      this.captureTimer = null;
      void this.capture('auto').finally(() => this.armCapture());
    }, this.captureIntervalMs);
  }

  private disarmCapture(): void {
    if (this.captureTimer === null) return;
    this.clearTimeoutFn(this.captureTimer);
    this.captureTimer = null;
  }

  private armWatchdog(): void {
    if (this.stopped || this.watchdogTimer !== null) return;
    this.watchdogTimer = this.setTimeoutFn(() => {
      this.watchdogTimer = null;
      this.checkSignalingAge();
      this.armWatchdog();
    }, SEVERE_WATCHDOG_MS);
  }
}
